import { FC, useMemo } from "react";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { CustomDialog } from "../styled/CustomDialog";
import { SxProps } from "@mui/material";
import { Theme } from "@mui/system";
import { useAppTheme } from "../../utils/theme";
import LoadingOverlay from "../LoadingOverlay";

type BasicDialogProps = {
	title: string;
	children: React.ReactNode | string;
	/** Default actions are Cancel and Save */
	actions?: React.ReactNode;
	onCancel?: () => void;
	onSubmit?: () => void;
	cancelText?: string;
	submitText?: string;
	isLoading?: boolean;
	sx?: SxProps<Theme>;
};

export const BasicDialog: FC<BasicDialogProps> = (props) => {
	const { cancelText = "CANCEL", submitText = "SAVE" } = props;
	const isLoading = useMemo(() => props.isLoading || false, [props.isLoading]);
	const theme = useAppTheme();

	return (
		<LoadingOverlay loading={isLoading}>
			<CustomDialog
				sx={{
					"& .MuiDialog-paper": {
						width: "100%",
						maxWidth: 370,
					},
					"& .MuiDialogContent-root": {
						padding: "16px",
					},
					"& .MuiDialogActions-root": {
						padding: "16px",
					},
					...props.sx,
				}}
				open={true}
			>
				<DialogTitle
					sx={{ m: 0, p: 2 }}
					id="add-card-dialog-title"
					variant="headlineSmall"
					color={(theme) => theme.palette.surface.light}
					bgcolor={(theme) => theme.palette.primary.dark}
				>
					{props.title}
				</DialogTitle>
				{!isLoading && (
					<IconButton
						aria-label="close"
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.surface.light,
						}}
						onClick={props.onCancel}
					>
						<CloseIcon />
					</IconButton>
				)}

				<DialogContent>{props.children}</DialogContent>
				<DialogActions
					sx={{
						borderTop: 1,
						borderColor: theme.palette.outline.main,
					}}
				>
					{props.actions ? (
						props.actions
					) : (
						<>
							<Button size="large" variant="outlined" onClick={props.onCancel}>
								{cancelText}
							</Button>
							<Button
								size="large"
								color="primary"
								variant="contained"
								sx={{ textTransform: "none", textAlign: "center" }}
								onClick={props.onSubmit}
							>
								{submitText}
							</Button>
						</>
					)}
				</DialogActions>
			</CustomDialog>
		</LoadingOverlay>
	);
};
